import { useState, useEffect } from "react";
import { motion } from "framer-motion";

interface Section {
  id: string;
  label: string;
}

interface SectionNavigationDotsProps {
  sections: Section[];
}

const SectionNavigationDots = ({ sections }: SectionNavigationDotsProps) => {
  const [activeSection, setActiveSection] = useState<string>(sections[0]?.id || "");

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 3;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        const element = document.getElementById(section.id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (sections.length === 0) return null;

  return (
    <>
      <style>{`
        .section-nav-container {
          position: fixed;
          top: 50%;
          transform: translateY(-70%);
          display: none;
          flex-direction: column;
          align-items: flex-end;
          z-index: 50;
          pointer-events: auto;
        }

        @media (min-width: 768px) {
          .section-nav-container {
            display: flex;
            right: 20px;
            gap: 12px;
          }
        }

        @media (min-width: 1024px) {
          .section-nav-container {
            right: 32px;
            gap: 16px;
          }
        }
        
        /* Hide dots when help menu is open */
        body.help-menu-open .section-nav-container {
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease;
        }

        .section-nav-button {
          display: flex;
          align-items: center;
          gap: 10px;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 4px;
          transition: all 0.3s ease;
        }

        .section-nav-button:focus-visible {
          outline: 2px solid rgba(185, 28, 28, 0.5);
          outline-offset: 2px;
          border-radius: 4px;
        }

        .section-nav-label {
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.5px;
          padding: 4px 8px;
          border-radius: 4px;
          white-space: nowrap;
          opacity: 0;
          transform: translateX(10px);
          transition: all 0.3s ease;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          backdrop-filter: blur(4px);
        }

        .section-nav-button:hover .section-nav-label,
        .section-nav-button.active .section-nav-label {
          opacity: 1;
          transform: translateX(0);
        }

        .section-nav-button.active .section-nav-label {
          background: hsl(var(--primary));
        }

        .section-nav-dot-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .section-nav-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(185, 28, 28, 0.3);
          border: 2px solid rgba(185, 28, 28, 0.5);
          transition: all 0.3s ease;
        }

        @media (min-width: 1024px) {
          .section-nav-dot {
            width: 10px;
            height: 10px;
          }
        }

        .section-nav-button:hover .section-nav-dot {
          transform: scale(1.2);
          background: rgba(185, 28, 28, 0.6);
        }

        .section-nav-button.active .section-nav-dot {
          background: hsl(var(--primary));
          border-color: hsl(var(--primary));
          transform: scale(1.3);
          box-shadow: 0 0 8px rgba(185, 28, 28, 0.5);
        }

        .section-nav-glow {
          position: absolute;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: rgba(185, 28, 28, 0.3);
          filter: blur(4px);
        }
      `}</style>

      <nav 
        className="section-nav-container"
        role="navigation"
        aria-label="Navegación de secciones"
      >
        {sections.map((section) => {
          const isActive = activeSection === section.id;
          return (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              className={`section-nav-button ${isActive ? 'active' : ''}`}
              aria-label={`Ir a sección ${section.label}`}
              aria-current={isActive ? "true" : undefined}
            >
              <span className="section-nav-label">
                {section.label}
              </span>
              
              <div className="section-nav-dot-wrapper">
                {isActive && (
                  <motion.div 
                    layoutId="navDotGlow"
                    className="section-nav-glow"
                    transition={{ duration: 0.3 }}
                  />
                )}
                <div className="section-nav-dot" />
              </div>
            </button>
          );
        })}
      </nav>
    </>
  );
};

export default SectionNavigationDots;
